// pageMeta.ts - the title, description and share image of every public page.
//
// One entry per path publicPaths() lists. The build's pre-render
// (scripts/prerender.mjs, via src/entry-server.tsx) writes these into each
// page's static <head>; applyPageHead keeps the live document in step after
// client-side navigation. Docs, feature, blog and legal pages derive theirs
// from the same data their screens render, so a new page of those kinds
// needs no entry here. tests/unit/prerender.test.tsx holds every page to the
// SEO baseline: a unique title of 15 to 60 characters and a unique
// description of 70 to 160.
import { DOC_PAGES } from '../docs/docsContent';
import { BLOG_POSTS } from '../marketing/screens-blog';
import { LEGAL_DOCS } from '../marketing/screens-company';
import { FEATURE_PILLARS } from '../marketing/screens-feature-pages';
import { SITE_ORIGIN, publicPaths } from './publicRoutes';

export interface PageMeta {
  /** Path as the sitemap lists it, e.g. `/docs/quickstart`. */
  path: string;
  title: string;
  description: string;
  /** Absolute https URL of the share image. */
  image: string;
  imageAlt: string;
  /** Twitter card type: a large image for landscape art, else a summary. */
  card: 'summary' | 'summary_large_image';
}

export const SITE_NAME = 'Soulstack Studio';

/** What the shell (index.html) says; used for every non-public route. */
export const DEFAULT_TITLE = 'Soulstack Studio';
export const DEFAULT_DESCRIPTION =
  'A reusable, GUI-first WebGL game engine and hosted visual Studio for creating games in the browser.';

const DEFAULT_IMAGE = { image: `${SITE_ORIGIN}/icon-512.png`, imageAlt: 'The Soulstack Studio logo', card: 'summary' as const };

const TITLE_MAX = 60;
const DESCRIPTION_MAX = 160;

/** `<name> | Soulstack Studio`, or the name alone when that would be too long. */
function titled(name: string, suffix = SITE_NAME): string {
  const full = `${name} | ${suffix}`;
  return full.length <= TITLE_MAX ? full : name;
}

/**
 * Trim copy to the description limit at a sentence end, so a long lede still
 * reads as whole sentences. Copy already within the limit is returned as is.
 */
export function fitDescription(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= DESCRIPTION_MAX) return clean;
  const cut = clean.slice(0, DESCRIPTION_MAX);
  const end = cut.lastIndexOf('. ');
  return end > 0 ? cut.slice(0, end + 1) : cut.slice(0, cut.lastIndexOf(' ')).replace(/[,;:]$/, '') + '.';
}

function poster(slug: string, file: string, name: string) {
  return {
    image: `${SITE_ORIGIN}/media/${slug}/${file}`,
    imageAlt: `${name} key art`,
    card: 'summary_large_image' as const,
  };
}

type Entry = Omit<PageMeta, 'path' | 'image' | 'imageAlt' | 'card'> & Partial<Pick<PageMeta, 'image' | 'imageAlt' | 'card'>>;

/** The fixed marketing pages: copy taken from each page's own heading and lede. */
const MARKETING: Record<string, Entry> = {
  '/': {
    title: 'Soulstack Studio | Make games in your browser',
    description:
      'A complete 2D and 3D game studio in a tab. Build worlds, characters, rules and multiplayer without code, then publish once as an installable app.',
  },
  '/features': {
    title: titled('Features'),
    description:
      'Scene, art, characters, rules, multiplayer and hosting, in one browser tab. Here is what each part of Soulstack Studio does.',
  },
  '/pricing': {
    title: titled('Pricing'),
    description:
      'Every part of Studio, including hosting, installable games and multiplayer, is free during early access. Paid plans will be announced here first.',
  },
  '/showcase': {
    title: 'Showcase: games made with Soulstack Studio',
    description:
      'Games made with Soulstack Studio. Every game here runs in a browser tab, hosted on the platform it was built on.',
  },
  '/sunder': {
    title: titled('Sunder'),
    description:
      'Sunder: five-a-side tactical combat in collapsing arenas. Read the map, hold the line, break theirs. A Soulstack Studio original.',
    ...poster('sunder', 'sunder-trailer-poster.jpg', 'Sunder'),
  },
  '/ninelives': {
    title: titled('Nine Lives'),
    description:
      'Nine Lives: a black cat escapes a cursed city. Each death unlocks a new ability. Difficult but fair precision platforming.',
    ...poster('nine-lives', 'ninelives-trailer-poster.jpg', 'Nine Lives'),
  },
  '/furrow': {
    title: titled('Furrow'),
    description:
      'Furrow: a shared valley for one to four cats. Farm, forage, fish and build alongside friends, on foot with tools in paw or on all fours.',
    ...poster('furrow', 'furrow-trailer-poster.jpg', 'Furrow'),
  },
  '/dwindling': {
    title: titled('The Dwindling'),
    description:
      "The Dwindling: one house, walked room to room. Your torch is the only light you get, and it doesn't stay lit forever.",
    ...poster('the-dwindling', 'dwindling-trailer-poster.jpg', 'The Dwindling'),
  },
  '/mewsli': {
    title: titled('Mewsli'),
    description:
      'Mewsli: a kawaii cat cafe match-3, five chapters deep. Match treats on the grid to serve a growing roster of cafe cats.',
    ...poster('mewsli', 'mewsli-trailer-poster.jpg', 'Mewsli'),
  },
  '/blog': {
    title: titled('Blog'),
    description:
      'Notes from the stack: product, engineering and design posts from the team building Soulstack Studio, its engine and its hosting.',
  },
  '/changelog': {
    title: titled('Changelog'),
    description:
      'What changed for creators and players in each Soulstack Studio release, from new workspaces and templates to fixes in published games.',
  },
  '/about': {
    title: titled('About'),
    description:
      'Soulstack is a small team building the whole stack: the engine, the browser studio, and the hosting your games run on.',
  },
  '/contact': {
    title: titled('Contact'),
    description:
      'Talk to the Soulstack Studio team about sales, support or studio partnerships. We reply within one working day.',
  },
  '/attributions': {
    title: titled('Attributions'),
    description:
      'Third-party creative assets used in Soulstack Studio and the games built on it, with the credit each licence requires.',
  },
};

/** One-line summaries of the legal documents, from their section headings. */
const LEGAL_SUMMARIES: Record<keyof typeof LEGAL_DOCS, string> = {
  terms:
    'The terms that govern every Soulstack Studio account and published game: your content, acceptable behaviour, plans and billing, and termination.',
  privacy:
    'What Soulstack Studio collects, what it never does with it, where data lives, your rights over it, and the cookies the studio uses.',
  aup: 'What published games on Soulstack Studio must not contain, the fair play and infrastructure rules, and how violations are enforced.',
};

function entryFor(path: string): Entry | null {
  const marketing = MARKETING[path];
  if (marketing) return marketing;

  const doc = DOC_PAGES.find((p) => p.path === path);
  if (doc) {
    if (path === '/docs') {
      return { title: doc.title, description: `${doc.summary} The guide to building, publishing and running games with Soulstack Studio.` };
    }
    const title = doc.title.length + ' | Studio docs'.length <= TITLE_MAX ? doc.title : doc.label;
    return {
      title: `${title} | Studio docs`,
      description: fitDescription(`${doc.summary} Part of the Soulstack Studio documentation.`),
    };
  }

  const feature = /^\/features\/([^/]+)$/.exec(path);
  if (feature) {
    const pillar = FEATURE_PILLARS[feature[1]!];
    if (pillar) return { title: titled(pillar.name), description: fitDescription(pillar.sub) };
  }

  const post = /^\/blog\/post\/([^/]+)$/.exec(path);
  if (post) {
    const found = BLOG_POSTS.find((p) => p.id === post[1]);
    if (found) return { title: titled(found.title), description: fitDescription(found.blurb) };
  }

  const legal = /^\/legal\/([^/]+)$/.exec(path);
  if (legal && legal[1]! in LEGAL_DOCS) {
    const key = legal[1] as keyof typeof LEGAL_DOCS;
    return { title: titled(LEGAL_DOCS[key].title), description: LEGAL_SUMMARIES[key] };
  }

  return null;
}

/** The head data for a public path, or null for any other route. */
export function pageMeta(path: string): PageMeta | null {
  const entry = entryFor(path);
  if (!entry) return null;
  return { path, ...DEFAULT_IMAGE, ...entry };
}

/** Head data for every public path, in sitemap order. */
export function allPageMeta(): PageMeta[] {
  return publicPaths().map((path) => {
    const meta = pageMeta(path);
    if (!meta) throw new Error(`No page meta for public path ${path}`);
    return meta;
  });
}

function setMeta(attr: 'name' | 'property', key: string, content: string | null): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (content === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Bring document.head in line with the route after a client-side navigation:
 * a public page gets its own title, description, canonical and share tags; any
 * other route gets the shell's defaults and no canonical.
 */
export function applyPageHead(path: string): void {
  if (typeof document === 'undefined') return;
  const meta = pageMeta(path);
  document.title = meta?.title ?? DEFAULT_TITLE;
  setMeta('name', 'description', meta?.description ?? DEFAULT_DESCRIPTION);
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (meta) {
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = SITE_ORIGIN + path;
  } else {
    canonical?.remove();
  }
  setMeta('property', 'og:title', meta?.title ?? null);
  setMeta('property', 'og:description', meta?.description ?? null);
  setMeta('property', 'og:url', meta ? SITE_ORIGIN + path : null);
  setMeta('property', 'og:image', meta?.image ?? null);
  setMeta('property', 'og:image:alt', meta?.imageAlt ?? null);
  setMeta('name', 'twitter:card', meta?.card ?? null);
}
